import {
  Download,
  Edit2,
  Filter,
  Search,
  Trash2,
  UserIcon,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/ui/AppButton";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import { buildApiUrl } from "../../lib/utils";
import {
  Transaction,
  TransactionSearchRequest,
  TransactionSearchResponse,
} from "./@types";
import TransactionStats from "./components/TransactionStats";
import { useGetTransactionsMutation } from "./transactionApiSlice";

const TransactionDashboard: React.FC = () => {
  const navigate = useNavigate();
  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [dateAddedFilter, setDateAddedFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [openModal, setOpenModal] = useState("");

  // RTK Query mutation
  const [getTransaction, { data: partnerData, isLoading }] =
    useGetTransactionsMutation();

  const fetchTransaction = useCallback(async () => {
    const requestBody: TransactionSearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      keyword: searchTerm || undefined,
      dateAdded: dateAddedFilter || undefined,
    };

    try {
      await getTransaction(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch transaction:", err);
    }
  }, [currentPage, pageSize, searchTerm, getTransaction]);

  // Fetch transaction on component mount and when filters change
  useEffect(() => {
    fetchTransaction();
  }, [currentPage, pageSize, searchTerm, dateAddedFilter, fetchTransaction]);

  // Table columns configuration
  const columns: Column<Transaction>[] = [
    {
      key: "TransactionId",
      header: "Transaction ID",
      width: "24",
      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "userName",
      header: "Transactions name",
      width: "",
      render: (value: string, item: Transaction) => (
        <div className="flex items-center space-x-3">
          {item.customerImageUrl ? (
            <img
              src={buildApiUrl(item.customerImageUrl)}
              alt={`${item.customerName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">{item.customerName}</span>
            <span className="capitalize text-pColor text-[13px]">
              {item.customerEmail}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "createdOn",
      header: "Date/time",
      width: "24",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "transactionType",
      header: "Transaction type",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "amount",
      header: "Amount",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "currency",
      header: "Currency",
      width: "16",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "20",
      render: (value: string) => {
        const getStatusInfo = () => {
          if (!value)
            return {
              label: "Processing",
              color: "text-gray-200 border border-gray-200",
            };
          if (value === "Completed")
            return {
              label: "Completed",
              color: "text-green-800 border border-green-800",
            };
          if (value === "Processing")
            return {
              label: "Processing",
              color: "text-yellow-800 border border-yellow-800",
            };
          return {
            label: "Completed",
            color: "text-green-800 border border-green-800",
          };
        };

        const status = getStatusInfo();
        return (
          <span
            className={`w-[60px] inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}
          >
            {status.label}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      width: "12",
      align: "center" as const,
      render: (value: any, item: Transaction) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "Edit",
                onClick: () => {
                  setSelectedTransaction(item);
                  setOpenModal("edit");
                },
                icon: <Edit2 className="w-4 h-4" />,
              },
              {
                label: "Delete",
                onClick: () => {
                  setSelectedTransaction(item);
                  setOpenModal("delete");
                },
                icon: <Trash2 className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleExportCSV = () => {
    console.log("Export CSV functionality");
  };

  const response = partnerData?.data as TransactionSearchResponse | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        {/* Dashboard Cards */}
        <div className="">
          <TransactionStats />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">
              All transaction
            </h2>
          </div>

          <div className="flex gap-3">
            <AppButton
              appearance="soft"
              onClick={handleExportCSV}
              className="flex items-center px-4 py-2 border border-primary bg-white rounded-lg text-primary hover:bg-gray-50 transition-colors min-w-[146px] h-[38px]"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </AppButton>
          </div>
        </div>

        {/* All transaction section */}
        <div className="space-y-6 bg-white rounded-[10px] p-4">
          {/* Filters and Actions */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="text-[16px] pl-9 pr-4 py-2 border border-[#F6F6F6] bg-[#F6F6F6] rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary w-full sm:w-[367px] h-[42px]"
                />
              </div>

              {/* Filters */}
              <button
                // onClick={() => setOpenModal(true)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition"
              >
                <Filter className="h-4 w-4 text-gray-600" />
                <span className="text-sm text-gray-700 font-medium">
                  Filter
                </span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden space-y-6">
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              emptyMessage="No transaction found"
            />

            {/* Pagination */}
            {response && (
              <Pagination
                currentPage={response.currentPage}
                totalPages={response.totalPages}
                pageSize={response.pageSize}
                totalCount={response.totalCount}
                hasNextPage={response.hasNextPage}
                hasPreviousPage={response.hasPreviousPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionDashboard;
